import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getProjectInfoApi, manualAnswerApi } from '../../services/AxiosServices';
import './Preaudit.css';
import PreauditLeftPanel from './components/PreauditLeftPanel';
import PreauditRightPanel from './components/PreauditRightPanel';

const Preaudit: React.FC = () => {
  const [data, setData] = useState<any>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Resizing state
  const [leftPanelWidth, setLeftPanelWidth] = useState<number | string>('50%');
  const isResizing = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchProjectInfo = async () => {
      setIsLoading(true);
      setError(null);
      
      const searchParams = new URLSearchParams(location.search);
      const projectName = searchParams.get('project_name');

      if (!projectName) {
        setError("No project name provided in the URL.");
        setIsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response: any = await getProjectInfoApi(projectName, token);
        if (response) {
          // Support whether response is an array or an object containing a list/array
          let rawQuestions: any[] = [];
          if (Array.isArray(response)) {
              rawQuestions = response;
          } else if (response.questions) {
              rawQuestions = response.questions;
          } else if (response.result && Array.isArray(response.result)) {
              rawQuestions = response.result;
          }

          const mappedQuestions = rawQuestions.map((q: any, idx: number) => ({
            ...q,
            id: q.id || q.question_id || `Q${idx + 1}`,
            no: q.no || idx + 1,
            title: q.question || q.title || `Question ${idx + 1}`,
            reviewer: q.approver_id?.approved_name || q.approve_id?.approved_name || q.approved_name || response.approved_name || q.owner_name || q.reviewer || 'Assign reviewer',
            status: q.status || 'Review',
            answers: (
              (q.findings && q.findings.found_answers) || 
              q.found_answers || 
              q.answers || []
            ).map((ans: any, aIdx: number) => ({
              ...ans,
              id: ans.id || `A${idx}_${aIdx + 1}`,
              title: ans.answer || ans.title || '',
              documentName: ans.source || ans.documentName || '',
              snippet: ans.page_num ? `Page: ${ans.page_num}` : (ans.snippet || '')
            }))
          }));

          const projectInfo = {
            name: response.project_id || (response.projectInfo?.name) || projectName,
            owner: response.owner_name || (response.projectInfo?.owner) || 'Unknown',
            totalQuestions: response.total_questions ?? (response.projectInfo?.totalQuestions) ?? mappedQuestions.length,
            progressPercentage: response.progress_percentage ?? (response.projectInfo?.progressPercentage) ?? 0
          };
          
          let defaultReviewers = [
            "Simon Gruyer",
            "Robert Gouda",
            "Walter Maaslander",
            "Patricia Amsterdam",
            "Assign reviewer"
          ];
          
          // Ensure dynamically fetched reviewers (like approved_name) are included in the dropdown options
          const dynamicReviewers = mappedQuestions.map((q: any) => q.reviewer);
          defaultReviewers = Array.from(new Set([...defaultReviewers, ...dynamicReviewers]));

          const defaultStatuses = [
            "Review",
            "Approved",
            "Generating"
          ];

          const dataToSet = {
            projectInfo: projectInfo,
            reviewers: response.reviewers || defaultReviewers,
            statuses: response.statuses || defaultStatuses,
            questions: mappedQuestions
          };

          setData(dataToSet);
          if (dataToSet.questions && dataToSet.questions.length > 0) {
            const qWithAnswers = dataToSet.questions.find((q: any) => q.answers && q.answers.length > 0);
            setSelectedId(qWithAnswers ? qWithAnswers.id : dataToSet.questions[0].id);
          } else {
            setSelectedId(null);
          }
        } else {
          setError("No data received from the API.");
        }
      } catch (err) {
        console.error("Failed to fetch project info:", err);
        setError("Failed to load project data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjectInfo();
  }, [location.search, getAccessTokenSilently]);

  const handleSelectRow = (id: string) => {
    setSelectedId(id);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    if (!isCollapsed) {
      setLeftPanelWidth('300px');
    } else {
      setLeftPanelWidth('50%');
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (!data.questions || !selectedId) return;
    const index = data.questions.findIndex((item: any) => item.id === selectedId);
    if (index >= 0 && index < data.questions.length - 1) {
      setSelectedId(data.questions[index + 1].id);
    }
  };

  const handlePrev = () => {
    if (!data.questions || !selectedId) return;
    const index = data.questions.findIndex((item: any) => item.id === selectedId);
    if (index > 0) {
      setSelectedId(data.questions[index - 1].id);
    }
  };

  const handleConfirm = (_approvedAnswerId: string) => {
    if (!selectedId) return;
    const newData = { ...data };
    const index = newData.questions.findIndex((item: any) => item.id === selectedId);
    if (index >= 0) {
      // In a real app we'd save this to backend. Here we just set status
      newData.questions[index].status = 'Approved';
      setData(newData);
    }
  };

  const handleRevoke = () => {
    if (!selectedId) return;
    const newData = { ...data };
    const index = newData.questions.findIndex((item: any) => item.id === selectedId);
    if (index >= 0) {
      newData.questions[index].status = 'Review';
      setData(newData);
    }
  };

  const handleManualSubmit = async (text: string) => {
    if (!selectedId || !text.trim()) return;
    try {
      const token = await getAccessTokenSilently();
      const payload = new FormData();
      payload.append("qa_id", selectedId);
      payload.append("new_answer", text.trim());
      await manualAnswerApi(payload, token);
      
      // Optionally update the local state to reflect the status change
      const newData = { ...data };
      const index = newData.questions.findIndex((item: any) => item.id === selectedId);
      if (index >= 0) {
        newData.questions[index].status = 'Approved';
        setData(newData);
      }
    } catch (error) {
      console.error("Failed to submit manual answer", error);
    }
  };

  // Resizer handlers
  const startResizing = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (isCollapsed) return;
    isResizing.current = true;
  }, [isCollapsed]);

  const stopResizing = React.useCallback(() => {
    isResizing.current = false;
  }, []);

  const resize = React.useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing.current && containerRef.current && !isCollapsed) {
        const containerRect = containerRef.current.getBoundingClientRect();
        let newWidth = mouseMoveEvent.clientX - containerRect.left;
        const minWidth = 400;
        const maxWidth = containerRect.width - 400;
        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setLeftPanelWidth(`${newWidth}px`);
      }
    },
    [isCollapsed]
  );

  useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  const selectedItem = data.questions?.find((item: any) => item.id === selectedId);

  if (isLoading) {
    return (
      <div className="pa-container" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#94a3b8', fontSize: '18px' }}>Loading project data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pa-container" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#ef4444', fontSize: '18px' }}>{error}</p>
      </div>
    );
  }

  if (!data.questions || data.questions.length === 0) {
    return (
      <div className="pa-container" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#94a3b8', fontSize: '18px' }}>No questions found for this project.</p>
      </div>
    );
  }

  return (
    <div className="pa-container" ref={containerRef}>
      <div 
        className={`pa-left-panel ${isCollapsed ? 'collapsed' : ''}`}
        style={{ width: isCollapsed ? '300px' : leftPanelWidth }}
      >
        <PreauditLeftPanel 
          data={data}
          selectedId={selectedId}
          onSelectRow={handleSelectRow}
          isCollapsed={isCollapsed}
        />
        {!isCollapsed && (
          <div className="pa-resizer" onMouseDown={startResizing} />
        )}
      </div>

      <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
        <button className="pa-collapse-toggle" onClick={handleToggleCollapse}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: isCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.3s' }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <PreauditRightPanel 
          selectedItem={selectedItem}
          onNext={handleNext}
          onPrev={handlePrev}
          onConfirm={handleConfirm}
          onRevoke={handleRevoke}
          onManualSubmit={handleManualSubmit}
        />
      </div>
    </div>
  );
};

export default Preaudit;
